import React from 'react';
import {
  ArrowRight,
  Briefcase,
  CheckCircle2,
  Code2,
  FileSpreadsheet,
  FileText,
  FlaskConical,
  ListChecks,
  MousePointerClick,
  Rocket,
  ShieldCheck,
  Sparkles,
  Type,
} from 'lucide-react';

const flowSteps = [
  {
    title: 'Choose Input',
    copy: 'Create or select the application you are testing.',
    icon: Briefcase,
    tone: 'blue',
  },
  {
    title: 'Pick Testing Area',
    copy: 'Attach a BRD, text requirement, API contract, workbook, or captured flow.',
    icon: ListChecks,
    tone: 'cyan',
  },
  {
    title: 'Review & Refine',
    copy: 'Review generated coverage and align expected outcomes.',
    icon: FlaskConical,
    tone: 'amber',
  },
  {
    title: 'Export & Execute',
    copy: 'Produce cases, POMs, scripts, and runs from one application hub.',
    icon: Rocket,
    tone: 'emerald',
  },
];

const workspaceToneClasses = {
  blue: 'landing-workspace-card-blue',
  violet: 'landing-workspace-card-violet',
  sky: 'landing-workspace-card-sky',
  amber: 'landing-workspace-card-amber',
  emerald: 'landing-workspace-card-emerald',
  teal: 'landing-workspace-card-teal',
};

export const ProductLanding = ({
  onSelectPDF,
  onOpenApplications,
}) => {
  const handleOpenApplications = onOpenApplications || onSelectPDF;
  const productAreas = [
    {
      number: '01',
      eyebrow: 'Document intelligence',
      title: 'BRD Intelligence',
      copy: 'Attach requirements documents to an application, extract sections, review generated cases, and produce scripts.',
      outcome: 'Document → sections → cases → scripts',
      icon: FileText,
      tone: 'blue',
      featured: true,
    },
    {
      number: '02',
      eyebrow: 'Fast authoring',
      title: 'Text Requirements',
      copy: 'Add focused requirements for an application, include optional screenshots, then continue through cases, POM, scripts, and execution.',
      outcome: 'Requirement → cases → POM → scripts',
      icon: Type,
      tone: 'violet',
    },
    {
      number: '03',
      eyebrow: 'Contract testing',
      title: 'API Testing',
      copy: 'Attach Swagger or OpenAPI contracts to an application, generate operation-level cases, then create Playwright API scripts.',
      outcome: 'Contract → operation cases → API scripts',
      icon: Code2,
      tone: 'sky',
    },
    {
      number: '04',
      eyebrow: 'Workbook import',
      title: 'Excel Test Cases',
      copy: 'Import existing testcase workbooks into an application, map changing columns, review parsed cases, then continue into automation.',
      outcome: 'Workbook → mapped cases → automation',
      icon: FileSpreadsheet,
      tone: 'emerald',
    },
    {
      number: '05',
      eyebrow: 'Real user journeys',
      title: 'Test Feature',
      copy: 'Capture a real user flow for an application, review the recorded steps, then generate test cases and scripts.',
      outcome: 'Recorded flow → cases → scripts',
      icon: MousePointerClick,
      tone: 'teal',
    },
  ];

  return (
    <main className="page-shell landing-page-shell">
      <div className="landing-ambient landing-ambient-one" aria-hidden="true" />
      <div className="landing-ambient landing-ambient-two" aria-hidden="true" />

      <div className="landing-container">
        <section className="landing-hero-shell" aria-labelledby="landing-title">
          <div className="landing-hero-grid" aria-hidden="true" />

          <div className="landing-hero-copy">
            <p className="landing-kicker">
              <span className="landing-kicker-icon" aria-hidden="true">
                <Sparkles className="h-3.5 w-3.5" />
              </span>
              TestSuite AI by MetaZ
            </p>

            <h1 id="landing-title" className="landing-title">
              Test every application from one{' '}
              <span className="landing-title-accent">shared hub</span>.
            </h1>

            <p className="landing-subtitle">
              Select the product you are testing, then run BRD, Text,
              API, Excel, and captured feature flows under the same shared
              application context.
            </p>

            <div className="landing-primary-action">
              <button
                type="button"
                onClick={handleOpenApplications}
                className="landing-cta-primary"
              >
                <span className="landing-cta-primary-icon" aria-hidden="true">
                  <Briefcase className="h-4 w-4" />
                </span>
                <span>Get Started</span>
                <ArrowRight className="landing-cta-arrow h-4 w-4" />
              </button>
              <p>Choose an application first, then start the right testing flow.</p>
            </div>

            <div className="landing-secondary-actions">
              <p className="landing-action-label">Testing areas included</p>
              <ul className="landing-cta-row">
                {productAreas.map((area) => {
                  const AreaIcon = area.icon;
                  return (
                    <li key={area.number} className="landing-area-chip">
                      <AreaIcon className="h-4 w-4" aria-hidden="true" />
                      {area.title}
                    </li>
                  );
                })}
              </ul>
            </div>

            <ul className="landing-proof-grid" aria-label="Product benefits">
              <li className="landing-proof-item">
                <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                <span>Positive and negative coverage</span>
              </li>
              <li className="landing-proof-item">
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                <span>AI-assisted authoring</span>
              </li>
              <li className="landing-proof-item">
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                <span>Export-ready artifacts</span>
              </li>
            </ul>
          </div>

          <aside
            className="landing-flow-panel"
            aria-labelledby="landing-flow-title"
          >
            <div className="landing-flow-header">
              <p className="landing-flow-kicker">Application flow</p>
              <span className="landing-flow-status">
                <span aria-hidden="true" />
                Workflow ready
              </span>
            </div>

            <h2 id="landing-flow-title" className="landing-flow-title">
              How teams test an application
            </h2>
            <p className="landing-flow-subtitle">
              A simple path from product context to testing deliverables.
            </p>

            <ol className="landing-flow-stack">
              {flowSteps.map((step, index) => {
                const StepIcon = step.icon;
                return (
                  <li className="landing-flow-step" key={step.title}>
                    <div className={`landing-flow-icon landing-flow-icon-${step.tone}`}>
                      <StepIcon className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <div className="landing-flow-step-content">
                      <div className="landing-flow-step-heading">
                        <p className="landing-flow-step-title">{step.title}</p>
                        <span>0{index + 1}</span>
                      </div>
                      <p className="landing-flow-step-copy">{step.copy}</p>
                    </div>
                  </li>
                );
              })}
            </ol>

            <div className="landing-flow-output">
              <div className="landing-flow-output-icon" aria-hidden="true">
                <Rocket className="h-4 w-4" />
              </div>
              <div>
                <p>Application-ready delivery</p>
                <span>Test cases · POM · Automation scripts</span>
              </div>
            </div>
          </aside>
        </section>

        <section
          className="landing-workspaces-section"
          aria-labelledby="landing-workspaces-title"
        >
          <div className="landing-section-heading">
            <div>
              <p className="landing-section-kicker">Application-first testing</p>
              <h2 id="landing-workspaces-title">Choose how to test an application</h2>
            </div>
            <p className="landing-section-copy">
              Start with the artifact you already have. TestSuite AI keeps the
              work tied to the application.
            </p>
          </div>

          <div className="landing-workspace-grid">
            {productAreas.map((workspace) => {
              const WorkspaceIcon = workspace.icon;
              return (
                <article
                  key={workspace.number}
                  className={`landing-workspace-card ${workspaceToneClasses[workspace.tone]} ${
                    workspace.featured ? 'landing-workspace-card-featured' : ''
                  }`}
                >
                  <div className="landing-workspace-card-glow" aria-hidden="true" />
                  <div className="landing-workspace-card-top">
                    <div className="landing-workspace-icon">
                      <WorkspaceIcon className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <span className="landing-workspace-number">{workspace.number}</span>
                  </div>

                  <p className="landing-workspace-eyebrow">{workspace.eyebrow}</p>
                  <h3>{workspace.title}</h3>
                  <p className="landing-workspace-copy">{workspace.copy}</p>

                  <p className="landing-workspace-outcome">
                    <span>{workspace.outcome}</span>
                  </p>
                </article>
              );
            })}
          </div>
        </section>
      </div>
    </main>
  );
};
